/**
 * ReferenceSelector — Unified reference selector modal for canvas-v2.
 *
 * A centered viewport overlay with three media-type tabs (Image/Video/Audio),
 * an "On canvas" grid of selectable asset cards, and a summary row with
 * dnd-kit drag-to-reorder. Selection is saved explicitly via "Save & Close";
 * dismissing via X or Escape discards changes.
 *
 * Upload button (reference mode only): opens a flyout to upload Image/Video/Audio.
 * Creates a node on the canvas in the background. Shows a spinning card in the grid
 * while uploading; auto-selects on completion; removes card + shows toast on error.
 *
 * Replaces both ImagePickerModal and ReferenceImagePicker from V1.
 */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNodes } from 'reactflow'
import { getThumbnailUrl, getLibraryThumbnailUrl } from '../../../../api/useAssetUrl'
import { VideoReferenceThumbnail } from './VideoReferenceThumbnail'
import { AudioReferenceThumbnail } from './AudioReferenceLabelChip'
import { authenticatedFetch, getConfiguredApiBase } from '../../../../api/client'
import {
  buildCandidates,
  buildAssetCandidates,
  getAudioOutputPort,
  getImageOutputPort,
  getVideoOutputPort,
  type CanvasAssetCandidate,
  type MentionCandidate,
  type MediaType,
} from './mentionUtils'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useLibraryYDoc } from '../../../../hooks/useLibraryYDoc'
import type { ReactFlowCanvasNode, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { useCanvasOptional } from '../../CanvasContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { stripDirectAssetInputPortWrites } from '../../assetPortGuards'
import { nodeSchemas, type RawNodeDataRecord } from '../../nodeRegistry'
import { UploadFlyout } from '../../../shared/UploadFlyout'
import { createNadDebug } from '../../../../utils/debugLog'
import { getAcceptedReferenceMediaTypes } from './nodeReferenceCapabilities'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { NODE_TYPE_ICON } from './nodeTypeIcons'
import { usePaletteAnalytics } from '../../../../analytics/paletteAnalytics'
export type { MediaType } from './mentionUtils'

const log = createNadDebug('dw:refselector:upload')
const removeBadgeLog = createNadDebug('dw:reference-selector:remove-badge')
const referenceSyncLog = createNadDebug('dw:reference-sync:selector')
const tabsLog = createNadDebug('dw:reference-tabs')
const drawerDismissLog = createNadDebug('dw:asset-manager:drawer')
const EMPTY_REACTIVE_NODES: ReactFlowCanvasNode[] = []

/**
 * Summary-card remove badges sit outside the card box at `-top-1.5` (6px) with a
 * 1px border, so they overhang the top of the selected tray by 7px.
 *
 * The tray is a horizontal scroller (`overflow-x-auto`). Per the CSS overflow
 * spec, a `visible` value on one axis computes to `auto` when the other axis is
 * not `visible`, so the tray clips vertically too and the badge renders cropped
 * unless the tray reserves at least that much top padding.
 */
export const SUMMARY_BADGE_OVERHANG_PX = 7
/** Tailwind `pt-2`. Must stay >= {@link SUMMARY_BADGE_OVERHANG_PX}. */
export const SUMMARY_TRAY_PADDING_TOP_PX = 8
export const SUMMARY_TRAY_PADDING_TOP_CLASS = 'pt-2'

/**
 * One id per opened picker, so a tray's render/remove events group into a single
 * `nad trace --expand` chain instead of arriving as unrelated lines.
 */
function useSummaryTraceId(isOpen: boolean): () => string {
  const ref = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen) ref.current = null
  }, [isOpen])
  return useCallback(() => (ref.current ??= `refsel-${crypto.randomUUID().slice(0, 8)}`), [])
}

interface ReactiveNodesSubscriberProps {
  onNodesChange: (nodes: ReactFlowCanvasNode[]) => void
}

function ReactiveNodesSubscriber({ onNodesChange }: ReactiveNodesSubscriberProps) {
  const reactiveNodes = useNodes<ReactFlowCanvasNodeData>()

  useEffect(() => {
    onNodesChange(reactiveNodes)
  }, [onNodesChange, reactiveNodes])

  return null
}

function referenceSyncTraceId(targetId: string): string {
  return `reference-sync:${targetId}`
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SelectedReference {
  nodeId: string
  nodeType: string
  label: string
  assetPath: string
  mediaType: MediaType
  /** Exact schema output port that owns this asset. */
  sourceHandle?: string
  /** Item index when the source port is an array output. */
  fromIndex?: number
  source?: SourceTab
  /** Non-media kind (e.g. 'script') for items with no thumbnail; mirrors
   *  CanvasAssetCandidate so the summary tray renders the text preview rather
   *  than a broken image. */
  nonMediaKind?: 'script'
  /** Short text preview for nonMediaKind items. */
  textPreview?: string
}

export interface SelectedNodeReference {
  nodeId: string
  nodeType: string
  label: string
  thumbnailPath?: string
}

type ReferenceLimitMap = Partial<Record<MediaType, number>>

interface ImportReferenceResponse {
  node_type: string
  node_data: RawNodeDataRecord
  assets_copied: number
}

export interface ReferenceSelectorProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback to close the modal (without saving) */
  onClose: () => void
  /** Callback with selected references when user clicks Save & Close */
  onSave: (references: SelectedReference[]) => void
  /** The node that opened the modal (excluded from grid) */
  excludeNodeId: string
  /** All canvas nodes to scan for assets */
  canvasNodes: ReactFlowCanvasNode[]
  /** Current saved references (pre-populates the summary row) */
  currentReferences?: SelectedReference[]
  /** Maximum number of usable references (soft limit), or null for no total cap. Default: 4 */
  maxReferences?: number | null
  /**
   * Slots the product holds back from the provider budget, for disclosure.
   *
   * RFC-1202 §5: the user is capped at 15 of Image Pro Plus's 16 because the
   * style preview occupies the last slot. Without saying so, the cap reads as
   * an arbitrary Dreamwrite number — and the style reference is exactly what
   * used to be dropped first when the old code truncated silently.
   */
  reservedStyleSlots?: number
  /** Hard per-media caps. Used when a target supports a smaller media-specific limit. */
  maxReferencesByMediaType?: ReferenceLimitMap
  /** Optional predicate to remove target-specific invalid asset candidates. */
  referenceCandidateFilter?: (candidate: CanvasAssetCandidate) => boolean
  /** Optional upload media override. Defaults to visible reference media types. */
  uploadMediaTypes?: readonly MediaType[]
  /**
   * At a per-media cap ABOVE 1, refuse the new pick instead of evicting the
   * oldest. Caps of exactly 1 keep the swap — there it is the intended
   * single-select gesture, not data loss.
   *
   * Default `false` keeps the historical FIFO swap everywhere. Opt-in because
   * eviction reads as data loss at 3 image slots: the user picks a 4th
   * reference and their first silently disappears, with the count still
   * reading 3 so the existing over-limit warning never fires.
   *
   * RFC-1202 made this urgent for video: declaring Veo's cap of 3 turned a
   * branch that never evicted (no declared image limit -> plain append) into
   * one that evicts on every 4th pick.
   */
  refuseAtMediaLimit?: boolean
  /** Restrict which media-type tabs are shown (default: all) */
  visibleTabs?: MediaType[]
  /**
   * Backend node type of the node opening the picker. When provided (and
   * `visibleTabs` is not), the picker hides media-type tabs the target node
   * cannot consume — e.g. an image_generator never sees a Video tab.
   * Derived from the generated schema field specs, so adding a video input
   * port to a schema automatically enables its Video tab.
   */
  targetBackendType?: string
  /** Operating mode: 'reference' for node input selection, 'asset-manager' for library browsing */
  mode?: 'reference' | 'asset-manager' | 'node-selector'
  /** Callback when user clicks "Add to canvas" in asset-manager mode */
  onAddToCanvas?: (references: SelectedReference[]) => void
  /** Callback to delete a node from the library (asset-manager mode) */
  onDeleteFromLibrary?: (nodeId: string) => void
  /**
   * Initial tab to open on (asset-manager mode). Used when the Director opens
   * the picker for a specific kind, e.g. "character" or "script", so the user
   * lands on the relevant tab. Falls back to the computed default when absent
   * or not currently visible.
   */
  initialTab?: TabKey
  /** Upload callback: creates a node on the canvas and returns the node ID */
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => string
  /**
   * Upload-completion callback for reference mode. Unlike `onSave`, this is
   * invoked without closing the selector so uploads created from "Add reference"
   * can be linked to the target node as soon as the upload node finishes.
   */
  onUploadCompleteReferencesChange?: (references: SelectedReference[]) => void
  /** Current selections when mode="node-selector". */
  currentNodeSelections?: SelectedNodeReference[]
  /** Save callback when mode="node-selector". */
  onSaveNodeSelections?: (references: SelectedNodeReference[]) => void
  /** Optional predicate for mode="node-selector" callers to hide ineligible nodes. */
  nodeSelectorFilter?: (node: ReactFlowCanvasNode) => boolean
  /** Optional title when mode="node-selector". */
  nodeSelectorTitle?: string
  /** Optional empty state text when mode="node-selector". */
  nodeSelectorEmptyLabel?: string
  /** Optional no-selection text when mode="node-selector". */
  nodeSelectorNoSelectionLabel?: string
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

/** Union of all tab keys: node-type tabs (library mode) + media-type tabs */
export type TabKey = 'character' | 'location' | 'object' | 'script' | MediaType

/** Mapping from node-type tab keys to the nodeType string used for filtering */
const NODE_TYPE_TAB_FILTER: Record<string, string> = {
  character: 'characterDesigner',
  location: 'locationDesigner',
  object: 'objectDesigner',
}

interface TabDef {
  key: TabKey
  label: string
  icon: React.ReactNode
}

export const IMAGE_ICON = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
)
export const VIDEO_ICON = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
)
export const AUDIO_ICON = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
  </svg>
)

/** Media-type tabs — used in 'reference' mode */
const MEDIA_TABS: Array<TabDef & { key: MediaType }> = [
  { key: 'image', label: 'Image', icon: IMAGE_ICON },
  { key: 'video', label: 'Video', icon: VIDEO_ICON },
  { key: 'audio', label: 'Audio', icon: AUDIO_ICON },
]
const ALL_MEDIA_TYPES: readonly MediaType[] = ['image', 'video', 'audio']
const MEDIA_SELECTED_COUNT_KEYS: Record<MediaType, string> = {
  image: 'referenceSelector.selectedImageCount',
  video: 'referenceSelector.selectedVideoCount',
  audio: 'referenceSelector.selectedAudioCount',
}

/** Full tab set — used in 'asset-manager' mode (node-type tabs + media tabs) */
const LIBRARY_TABS: TabDef[] = [
  {
    key: 'character',
    label: 'Character',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    key: 'location',
    label: 'Location',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    key: 'object',
    label: 'Object',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  { key: 'image', label: 'Image', icon: IMAGE_ICON },
  { key: 'video', label: 'Video', icon: VIDEO_ICON },
  { key: 'audio', label: 'Audio', icon: AUDIO_ICON },
  {
    key: 'script',
    label: 'Script',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
]

type SourceTab = 'canvas' | 'library'
type ReferenceCandidate = CanvasAssetCandidate & { source: SourceTab }
const SOURCE_TABS: SourceTab[] = ['canvas', 'library']

// ─── Pending upload info ─────────────────────────────────────────────────────

interface PendingUpload {
  nodeId: string
  mediaType: MediaType
  fileName: string
  previewUrl?: string
}

function referenceSource(ref: Pick<SelectedReference, 'source'>): SourceTab {
  return ref.source ?? 'canvas'
}

function selectedReferenceKey(
  ref: Pick<SelectedReference, 'nodeId' | 'mediaType' | 'source' | 'sourceHandle' | 'fromIndex'>,
): string {
  return [
    referenceSource(ref),
    ref.nodeId,
    ref.mediaType,
    ref.sourceHandle ?? '',
    ref.fromIndex ?? '',
  ].join(':')
}

function withReferenceSource(candidate: CanvasAssetCandidate, source: SourceTab): ReferenceCandidate {
  return { ...candidate, source }
}

function summarizeReferences(refs: SelectedReference[]): string {
  if (refs.length === 0) return '[]'
  return refs
    .slice(0, 12)
    .map((ref) => (
      `${ref.nodeId}.${ref.sourceHandle ?? outputPortForReference(ref)}`
      + `${ref.fromIndex === undefined ? '' : `[${ref.fromIndex}]`}`
      + `:${ref.mediaType}:${ref.assetPath}`
    ))
    .join('|')
}

function selectedReferenceFromCandidate(candidate: ReferenceCandidate): SelectedReference {
  return {
    nodeId: candidate.nodeId,
    nodeType: candidate.nodeType,
    label: candidate.label,
    assetPath: candidate.assetPath,
    mediaType: candidate.mediaType,
    sourceHandle: candidate.sourceHandle,
    fromIndex: candidate.fromIndex,
    source: candidate.source,
    nonMediaKind: candidate.nonMediaKind,
    textPreview: candidate.textPreview,
  }
}

function normalizedReferenceLimit(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return Math.max(0, Math.floor(value))
}

function mediaReferenceLimit(
  maxReferencesByMediaType: ReferenceLimitMap | undefined,
  mediaType: MediaType,
): number | undefined {
  return normalizedReferenceLimit(maxReferencesByMediaType?.[mediaType])
}

function applyMediaReferenceLimits(
  refs: SelectedReference[],
  maxReferencesByMediaType?: ReferenceLimitMap,
): SelectedReference[] {
  const counts = new Map<MediaType, number>()
  const limited: SelectedReference[] = []
  let changed = false

  for (const ref of refs) {
    const limit = mediaReferenceLimit(maxReferencesByMediaType, ref.mediaType)
    const nextCount = (counts.get(ref.mediaType) ?? 0) + 1
    if (limit !== undefined && nextCount > limit) {
      changed = true
      continue
    }
    counts.set(ref.mediaType, nextCount)
    limited.push(ref)
  }

  return changed ? limited : refs
}

function addReferenceWithMediaLimits(
  refs: SelectedReference[],
  next: SelectedReference,
  maxReferencesByMediaType?: ReferenceLimitMap,
  refuseAtMediaLimit = false,
): SelectedReference[] {
  const limit = mediaReferenceLimit(maxReferencesByMediaType, next.mediaType)
  if (limit === undefined) return [...refs, next]
  if (limit <= 0) return refs

  const sameMediaRefs = refs.filter((ref) => ref.mediaType === next.mediaType)
  if (sameMediaRefs.length < limit) return [...refs, next]

  // At the cap. Returning `refs` unchanged is what the caller detects (same
  // array identity) to raise the refusal notice — silently dropping the user's
  // earliest reference is the behavior this opts out of.
  //
  // A cap of 1 is exempt: there, the swap IS the gesture. Clicking a different
  // reference video to replace the current one is deliberate single-select
  // (see "keeps video references single-select"), nothing is lost that the user
  // did not just replace on purpose, and refusing would strand them until they
  // found the remove button. Eviction only reads as data loss at 2+ slots,
  // where the dropped item is one the user chose SEPARATELY from this click.
  if (refuseAtMediaLimit && limit > 1) return refs

  const dropCount = sameMediaRefs.length - limit + 1
  const keysToDrop = new Set(
    sameMediaRefs
      .slice(0, dropCount)
      .map((ref) => selectedReferenceKey(ref)),
  )
  return [
    ...refs.filter((ref) => !keysToDrop.has(selectedReferenceKey(ref))),
    next,
  ]
}

function applySaveReferenceLimits(
  refs: SelectedReference[],
  maxReferences: number | null,
  maxReferencesByMediaType?: ReferenceLimitMap,
): SelectedReference[] {
  const totalLimit = normalizedReferenceLimit(maxReferences ?? undefined) ?? refs.length
  const counts = new Map<MediaType, number>()
  const limited: SelectedReference[] = []

  for (const ref of refs) {
    if (limited.length >= totalLimit) break
    const mediaLimit = mediaReferenceLimit(maxReferencesByMediaType, ref.mediaType)
    const nextMediaCount = (counts.get(ref.mediaType) ?? 0) + 1
    if (mediaLimit !== undefined && nextMediaCount > mediaLimit) continue
    counts.set(ref.mediaType, nextMediaCount)
    limited.push(ref)
  }

  return limited
}

function isReferenceOverMediaLimit(
  refs: SelectedReference[],
  index: number,
  maxReferencesByMediaType?: ReferenceLimitMap,
): boolean {
  const ref = refs[index]
  if (!ref) return false
  const limit = mediaReferenceLimit(maxReferencesByMediaType, ref.mediaType)
  if (limit === undefined) return false
  let count = 0
  for (let i = 0; i <= index; i += 1) {
    if (refs[i]?.mediaType === ref.mediaType) count += 1
  }
  return count > limit
}

function isAnyMediaReferenceLimitExceeded(
  refs: SelectedReference[],
  maxReferencesByMediaType?: ReferenceLimitMap,
): boolean {
  const counts = new Map<MediaType, number>()
  for (const ref of refs) {
    const nextCount = (counts.get(ref.mediaType) ?? 0) + 1
    counts.set(ref.mediaType, nextCount)
    const limit = mediaReferenceLimit(maxReferencesByMediaType, ref.mediaType)
    if (limit !== undefined && nextCount > limit) return true
  }
  return false
}

function referenceCountForMediaType(refs: SelectedReference[], mediaType: MediaType): number {
  return refs.filter((ref) => ref.mediaType === mediaType).length
}

function outputPortForReference(
  ref: Pick<SelectedReference, 'nodeType' | 'mediaType' | 'sourceHandle'>,
): string {
  if (ref.sourceHandle) return ref.sourceHandle
  if (ref.mediaType === 'video') return getVideoOutputPort(ref.nodeType)
  if (ref.mediaType === 'audio') return getAudioOutputPort(ref.nodeType)
  return getImageOutputPort(ref.nodeType)
}

function resolveAssetPreviewUrl(projectId: string, assetPath: string, isLibrary?: boolean): string | null {
  return isLibrary
    ? getLibraryThumbnailUrl(assetPath)
    : getThumbnailUrl(projectId, assetPath, 0)
}

const AUDIO_GRID_BAR_COUNT = 48
const AUDIO_SUMMARY_BAR_COUNT = 20

// ─── SortableSummaryCard ─────────────────────────────────────────────────────

const SortableSummaryCard = memo(function SortableSummaryCard({
  reference,
  projectId,
  onRemove,
  isDimmed,
  isLibrary,
}: {
  reference: SelectedReference
  projectId: string
  onRemove: (key: string) => void
  isDimmed: boolean
  isLibrary?: boolean
}) {
  const { t } = useTranslation('canvas')
  const sortableId = selectedReferenceKey(reference)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : isDimmed ? 0.35 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col items-center gap-1 p-1.5 rounded-lg border select-none cursor-grab active:cursor-grabbing w-20 flex-shrink-0 ${
        isDimmed
          ? 'border-white/5 bg-white/[0.02]'
          : 'border-white/10 bg-surface-1 hover:border-border'
      } ${isDragging ? 'shadow shadow-black/30' : ''}`}
      data-testid={`reference-summary-card-${reference.nodeId}`}
      data-media-type={reference.mediaType}
      {...attributes}
      {...listeners}
    >
      {/* Thumbnail */}
      {reference.nonMediaKind === 'script' ? (
        <div
          className="w-14 h-14 rounded bg-surface-2 border border-white/5 p-1 overflow-hidden text-left"
          data-testid={`reference-summary-text-preview-${reference.nodeId}`}
        >
          <p className="text-xxs leading-tight text-text-muted line-clamp-3 whitespace-pre-wrap break-words">
            {reference.textPreview || t('referenceSelector.script')}
          </p>
        </div>
      ) : reference.mediaType === 'image' ? (
        <img
          src={resolveAssetPreviewUrl(projectId, reference.assetPath, isLibrary) ?? undefined}
          alt={reference.label}
          className="w-14 h-14 rounded object-cover bg-black/30"
        />
      ) : reference.mediaType === 'video' ? (
        <VideoReferenceThumbnail
          projectId={projectId}
          nodeId={reference.nodeId}
          assetPath={reference.assetPath}
          alt={reference.label}
          className="w-14 h-14 rounded object-cover bg-black/30"
          surface="selected-reference-summary"
          isLibrary={isLibrary}
        />
      ) : (
        <AudioReferenceThumbnail
          label={reference.label}
          url={resolveAssetPreviewUrl(projectId, reference.assetPath, isLibrary)}
          barCount={AUDIO_SUMMARY_BAR_COUNT}
          referenceNodeId={reference.nodeId}
          surface="selected-reference-summary"
          labelPlacement="none"
          testId={`reference-summary-audio-thumbnail-${reference.nodeId}`}
          className="h-14 w-14 rounded bg-input-bg"
        />
      )}

      {/* Label */}
      <span className="text-xxs text-text-secondary truncate w-full text-center leading-tight">
        {reference.label}
      </span>

      {/* Remove button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onRemove(sortableId)
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-surface-2 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80"
        title={t('referenceSelector.remove')}
      >
        <svg className="w-2.5 h-2.5 text-foreground/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
})
SortableSummaryCard.displayName = 'SortableSummaryCard'

// ─── ReferenceSelector Component ─────────────────────────────────────────────

const AssetReferenceSelector = memo(function AssetReferenceSelector({
  isOpen,
  onClose,
  onSave,
  excludeNodeId,
  canvasNodes,
  currentReferences = [],
  maxReferences = 4,
  reservedStyleSlots,
  maxReferencesByMediaType,
  referenceCandidateFilter,
  uploadMediaTypes,
  refuseAtMediaLimit = false,
  visibleTabs,
  targetBackendType,
  mode = 'reference',
  onAddToCanvas,
  onDeleteFromLibrary,
  initialTab,
  onUploadFile,
  onUploadCompleteReferencesChange,
}: ReferenceSelectorProps) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const canvas = useCanvasOptional()
  // RFC-1577: in asset-manager mode this IS the palette's asset library, so its
  // actions are attributed to the toolbar click that opened it. Reference /
  // node-selector modes are node-panel surfaces and stay untracked here.
  const paletteAnalytics = usePaletteAnalytics()
  const libraryAnalytics = mode === 'asset-manager' ? paletteAnalytics : null
  const [sourceTab, setSourceTab] = useState<SourceTab>(mode === 'asset-manager' ? 'library' : 'canvas')
  const [isSaving, setIsSaving] = useState(false)

  // Effective tab restriction: explicit `visibleTabs` wins; otherwise derive
  // from the target node's accepted media types (schema-driven). Without this,
  // unsupported tabs would render and let users pick references that fail to
  // save (bug: Workplace post 1520084086415556).
  const effectiveVisibleTabs: MediaType[] | undefined = useMemo(() => {
    if (visibleTabs) return visibleTabs
    if (!targetBackendType) return undefined
    const derived = getAcceptedReferenceMediaTypes(targetBackendType)
    tabsLog('tabs-resolved %o', {
      targetBackendType,
      derivedTabs: derived,
      explicitVisibleTabs: null,
      finalTabs: derived,
    })
    return derived
  }, [visibleTabs, targetBackendType])

  // Determine tabs based on mode
  const activeTabs: TabDef[] = useMemo(() => {
    if (mode === 'asset-manager') return LIBRARY_TABS
    if (sourceTab === 'library' && !effectiveVisibleTabs) return LIBRARY_TABS
    return effectiveVisibleTabs
      ? MEDIA_TABS.filter((t) => effectiveVisibleTabs.includes(t.key))
      : MEDIA_TABS
  }, [mode, sourceTab, effectiveVisibleTabs])

  const allowedUploadMediaTypes = useMemo<readonly MediaType[]>(
    () => uploadMediaTypes ?? effectiveVisibleTabs ?? ALL_MEDIA_TYPES,
    [effectiveVisibleTabs, uploadMediaTypes],
  )

  // Default tab: 'character' for asset-manager, first visible tab or 'image' for reference
  const defaultTab: TabKey = useMemo(
    () => mode === 'asset-manager' || (sourceTab === 'library' && !effectiveVisibleTabs)
      ? 'character'
      : (activeTabs[0]?.key ?? 'image'),
    [mode, sourceTab, effectiveVisibleTabs, activeTabs]
  )

  const [activeTab, setActiveTab] = useState<TabKey>(
    // Honor an explicit initialTab (Director "open picker for kind X") on first
    // mount so the requested tab is active before the first paint.
    initialTab ?? defaultTab,
  )
  const [selected, setSelected] = useState<SelectedReference[]>([])
  const selectedRef = useRef<SelectedReference[]>([])
  const initializedRef = useRef(false)

  useEffect(() => {
    selectedRef.current = selected
  }, [selected])

  // Delete confirmation state
  const [pendingDeleteNodeId, setPendingDeleteNodeId] = useState<string | null>(null)

  // Suspend canvas keyboard shortcuts while this overlay is open. `isOpen` alone
  // is sufficient: the nested delete-confirmation dialog only renders past the
  // `if (!isOpen)` early return below, so gating on isOpen covers both the picker
  // and the delete dialog — and avoids a stuck guard if the selector is closed
  // (Escape/X) while a delete dialog is pending. Placed before the early return
  // so the hook always runs (Rules of Hooks). See canvasShortcutGuard.
  useBlockingOverlay(isOpen)

  // ── Pending uploads ────────────────────────────────────────────────────────
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])

  // Subscribe to reactive ReactFlow nodes while the selector is open. Hidden
  // selectors otherwise should not rerender on every drag frame.
  const shouldTrackReactiveNodes = isOpen || pendingUploads.length > 0
  const [reactiveNodes, setReactiveNodes] = useState<ReactFlowCanvasNode[]>(EMPTY_REACTIVE_NODES)
  const [reactiveNodesReady, setReactiveNodesReady] = useState(false)

  const handleReactiveNodesChange = useCallback((nextNodes: ReactFlowCanvasNode[]) => {
    setReactiveNodes((prevNodes) => {
      if (
        prevNodes.length === nextNodes.length &&
        prevNodes.every((node, index) => node === nextNodes[index])
      ) {
        return prevNodes
      }
      return nextNodes
    })
    setReactiveNodesReady(true)
  }, [])

  useEffect(() => {
    if (!shouldTrackReactiveNodes) {
      setReactiveNodes(EMPTY_REACTIVE_NODES)
      setReactiveNodesReady(false)
    }
  }, [shouldTrackReactiveNodes])

  // Use reactive nodes while open so newly generated/uploaded assets appear
  // without closing and reopening the selector. Fall back during the subscriber's
  // initial effect tick.
  const effectiveCanvasNodes = shouldTrackReactiveNodes && reactiveNodesReady
    ? reactiveNodes
    : canvasNodes

  useEffect(() => {
    setSourceTab(mode === 'asset-manager' ? 'library' : 'canvas')
  }, [mode, isOpen])

  // When opened with an explicit initialTab (Director "open picker for kind X"),
  // jump to that tab if it's currently selectable.
  useEffect(() => {
    if (isOpen && initialTab && activeTabs.some((tab) => tab.key === initialTab)) {
      setActiveTab(initialTab)
    }
  }, [isOpen, initialTab, activeTabs])

  useEffect(() => {
    if (!activeTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(defaultTab)
    }
  }, [activeTab, activeTabs, defaultTab])

  // Connect to library YDoc only while the visible selector needs it. The
  // component stays mounted when closed, so sourceTab alone would keep the
  // per-user _library/default WebSocket open after the library UI is hidden.
  // useLibraryYDoc internally gates on auth state to avoid reconnect loops
  // when the session expires.
  const { nodes: libraryNodes } = useLibraryYDoc({ enabled: isOpen && sourceTab === 'library' })

  // Reset internal state when modal opens — refresh labels from current candidates.
  // Only reset when opening (not while open) to avoid wiping user selections.
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      // Refresh labels from current buildAssetCandidates output to avoid stale labels
      const currentCandidates = buildAssetCandidates(effectiveCanvasNodes, excludeNodeId)
        .filter((candidate) => referenceCandidateFilter?.(candidate) ?? true)
        .map((candidate) => withReferenceSource(candidate, 'canvas'))
      const candidateLookup = new Map(
        currentCandidates.map((candidate): [string, ReferenceCandidate] => [
          selectedReferenceKey(candidate),
          candidate,
        ]),
      )
      const refreshed = currentReferences.map((ref) => {
        const fresh = candidateLookup.get(selectedReferenceKey(ref))
          ?? currentCandidates.find((candidate) => (
            candidate.nodeId === ref.nodeId
            && candidate.mediaType === ref.mediaType
            && candidate.assetPath === ref.assetPath
          ))
        return fresh
          ? {
              ...ref,
              label: fresh.label,
              assetPath: fresh.assetPath,
              sourceHandle: fresh.sourceHandle,
              fromIndex: fresh.fromIndex,
              source: referenceSource(ref),
            }
          : ref
      })
      const limited = applyMediaReferenceLimits(refreshed, maxReferencesByMediaType)
      referenceSyncLog({
        event: 'selector-open',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        mode,
        sourceTab,
        currentRefCount: currentReferences.length,
        refreshedRefCount: limited.length,
        candidateCount: candidateLookup.size,
        currentRefs: summarizeReferences(currentReferences),
        refreshedRefs: summarizeReferences(limited),
      })
      setSelected(limited)
      // Honor an explicit initialTab (Director "open picker for kind X") on
      // open; otherwise fall back to the computed default.
      setActiveTab(
        initialTab && activeTabs.some((tab) => tab.key === initialTab) ? initialTab : defaultTab,
      )
      // Revoke blob URLs before clearing pending uploads to avoid memory leaks
      setPendingUploads((prev) => {
        for (const pu of prev) {
          if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
        }
        return []
      })
      initializedRef.current = true
    } else if (!isOpen) {
      initializedRef.current = false
    }
  }, [isOpen, currentReferences, effectiveCanvasNodes, excludeNodeId, visibleTabs, activeTabs, mode, sourceTab, libraryNodes, defaultTab, initialTab, maxReferencesByMediaType, referenceCandidateFilter])

  // Build asset candidates from the active source
  const allCandidates = useMemo(() => {
    if (!isOpen && pendingUploads.length === 0) return []
    const sourceNodes = sourceTab === 'library' ? libraryNodes : effectiveCanvasNodes
    const excludeId = sourceTab === 'library' ? undefined : excludeNodeId
    // Library browse (asset-manager) surfaces non-media items (e.g. scripts) so
    // they're visible/disambiguable; reference-input pickers stay media-only.
    const includeNonMedia = mode === 'asset-manager'
    return buildAssetCandidates(sourceNodes, excludeId, includeNonMedia)
      .filter((candidate) => referenceCandidateFilter?.(candidate) ?? true)
      .map((candidate) => ({
        ...candidate,
        source: sourceTab,
      }))
  }, [isOpen, pendingUploads.length, sourceTab, libraryNodes, effectiveCanvasNodes, excludeNodeId, mode, referenceCandidateFilter])

  // ── Watch pending uploads for completion/error ─────────────────────────────
  useEffect(() => {
    if (pendingUploads.length === 0) return

    const remaining: PendingUpload[] = []
    const completedRefs: SelectedReference[] = []
    let changed = false

    for (const pu of pendingUploads) {
      const node = effectiveCanvasNodes.find((n) => n.id === pu.nodeId)
      const nodeStatus = typeof node?.data?.status === 'string' ? node.data.status : undefined

      if (nodeStatus === 'completed') {
        // Check if buildAssetCandidates finds an asset for this node
        const asset = node
          ? buildAssetCandidates([node]).find(
              (candidate) => (
                candidate.nodeId === pu.nodeId
                && (referenceCandidateFilter?.(candidate) ?? true)
              ),
            )
          : undefined
        if (asset) {
          if (!allowedUploadMediaTypes.includes(pu.mediaType)) {
            log({
              event: 'upload-complete-unsupported-type',
              trace_id: `refselector-upload:${pu.nodeId}`,
              nodeId: pu.nodeId,
              mediaType: pu.mediaType,
              allowedUploadMediaTypes,
              targetBackendType,
              sourceTab,
              activeTab: String(activeTab),
            })
            if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
            changed = true
            continue
          }
          log({
            event: 'upload-complete',
            trace_id: `refselector-upload:${pu.nodeId}`,
            projectId,
            targetId: excludeNodeId,
            nodeId: pu.nodeId,
            mediaType: pu.mediaType,
            sourceTab,
            activeTab: String(activeTab),
            allowedUploadMediaTypes,
          })
          // Revoke blob URL to avoid memory leak
          if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
          completedRefs.push({
            nodeId: asset.nodeId,
            nodeType: asset.nodeType,
            label: asset.label,
            assetPath: asset.assetPath,
            mediaType: asset.mediaType,
            sourceHandle: asset.sourceHandle,
            fromIndex: asset.fromIndex,
            source: 'canvas',
          })
          changed = true
          continue // remove from pending
        }
      }

      if (nodeStatus === 'error') {
        log({
          event: 'upload-error',
          trace_id: `refselector-upload:${pu.nodeId}`,
          nodeId: pu.nodeId,
          mediaType: pu.mediaType,
          sourceTab,
          activeTab: String(activeTab),
        })
        // Revoke blob URL to avoid memory leak
        if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
        // Remove from pending — the upload failed
        changed = true
        continue
      }

      remaining.push(pu)
    }

    if (changed) {
      setPendingUploads(remaining)
    }

    if (completedRefs.length === 0) return

    const previousSelected = selectedRef.current
    let nextSelected = previousSelected
    for (const completedRef of completedRefs) {
      const completedKey = selectedReferenceKey(completedRef)
      if (nextSelected.some((r) => selectedReferenceKey(r) === completedKey)) continue
      nextSelected = addReferenceWithMediaLimits(
        nextSelected,
        completedRef,
        maxReferencesByMediaType,
      )
    }

    const persistedSelected = nextSelected === previousSelected ? previousSelected : nextSelected

    if (nextSelected !== previousSelected) {
      selectedRef.current = nextSelected
      setSelected(nextSelected)
    }

    if (onUploadCompleteReferencesChange) {
      const canvasRefs = persistedSelected.filter((ref) => referenceSource(ref) === 'canvas')
      const autoLinkedRefs = applySaveReferenceLimits(
        canvasRefs,
        maxReferences,
        maxReferencesByMediaType,
      )
      log({
        event: 'upload-auto-link',
        trace_id: `refselector-upload:${completedRefs[0]?.nodeId ?? excludeNodeId}`,
        projectId,
        targetId: excludeNodeId,
        targetBackendType,
        uploadedRefIds: completedRefs.map((ref) => ref.nodeId).join(','),
        uploadedNodeIds: completedRefs.map((ref) => ref.nodeId).join(','),
        uploadedAssetPaths: completedRefs.map((ref) => ref.assetPath).join(','),
        mediaTypes: completedRefs.map((ref) => ref.mediaType).join(','),
        fromNodeIds: autoLinkedRefs.map((ref) => ref.nodeId).join(','),
        fromHandles: autoLinkedRefs.map(outputPortForReference).join(','),
        toIndexes: autoLinkedRefs.map((_ref, index) => index).join(','),
        selectedCount: persistedSelected.length,
        canvasRefCount: canvasRefs.length,
        savedCount: autoLinkedRefs.length,
        savedRefs: summarizeReferences(autoLinkedRefs),
        maxReferences,
      })
      onUploadCompleteReferencesChange(autoLinkedRefs)
    }
  }, [
    pendingUploads,
    effectiveCanvasNodes,
    allowedUploadMediaTypes,
    targetBackendType,
    sourceTab,
    activeTab,
    onUploadCompleteReferencesChange,
    maxReferences,
    maxReferencesByMediaType,
    excludeNodeId,
    projectId,
    referenceCandidateFilter,
  ])

  // ── Handle upload from flyout ──────────────────────────────────────────────
  const handleUpload = useCallback(
    (file: File, type: 'image' | 'video' | 'audio') => {
      if (!onUploadFile) return
      if (!allowedUploadMediaTypes.includes(type)) {
        log({
          event: 'upload-rejected-unsupported-type',
          trace_id: `refselector-upload:${targetBackendType ?? 'unknown'}:${type}`,
          type,
          fileName: file.name,
          targetBackendType,
          allowedUploadMediaTypes,
          sourceTab,
          activeTab: String(activeTab),
        })
        toast.error(t('referenceSelector.cannotUploadType', { type, allowed: allowedUploadMediaTypes.join(', ') || 'none' }))
        return
      }
      const nodeId = onUploadFile(file, type)
      setSourceTab('canvas')
      setActiveTab(type)
      log({
        event: 'upload-start',
        trace_id: `refselector-upload:${nodeId}`,
        projectId,
        targetId: excludeNodeId,
        nodeId,
        type,
        fileName: file.name,
        targetBackendType,
        allowedUploadMediaTypes,
        previousSourceTab: sourceTab,
        nextSourceTab: 'canvas',
        previousActiveTab: String(activeTab),
        nextActiveTab: type,
      })

      // Track as pending
      const previewUrl = file.type.startsWith('image/') || file.type.startsWith('video/')
        ? URL.createObjectURL(file)
        : undefined
      setPendingUploads((prev) => [
        ...prev,
        { nodeId, mediaType: type, fileName: file.name, previewUrl },
      ])

    },
    [onUploadFile, allowedUploadMediaTypes, targetBackendType, sourceTab, activeTab, projectId, excludeNodeId, t],
  )

  const handleSourceTabChange = useCallback(
    (nextSource: SourceTab) => {
      setSourceTab(nextSource)
      if (!visibleTabs) {
        setActiveTab(nextSource === 'library' ? 'character' : 'image')
      }
      referenceSyncLog({
        event: 'source-change',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        mode,
        sourceTab: nextSource,
      })
    },
    [excludeNodeId, mode, visibleTabs],
  )

  // Filter candidates by active tab (node-type tabs filter by nodeType, media tabs by mediaType)
  const filteredCandidates = useMemo(
    () => allCandidates.filter((c) => {
      // The Script tab maps to the non-media script candidates.
      if (activeTab === 'script') return c.nonMediaKind === 'script'
      // Non-media items never appear under media/node-type tabs.
      if (c.nonMediaKind) return false
      const nodeTypeFilter = NODE_TYPE_TAB_FILTER[activeTab]
      if (nodeTypeFilter) return c.nodeType === nodeTypeFilter
      return c.mediaType === activeTab
    }),
    [allCandidates, activeTab]
  )

  // Filter pending uploads by active tab
  const filteredPending = useMemo(
    () => pendingUploads.filter((pu) => pu.mediaType === activeTab),
    [pendingUploads, activeTab]
  )

  useEffect(() => {
    if (pendingUploads.length === 0) return
    log({
      event: 'pending-upload-visible',
      trace_id: `refselector-upload:${pendingUploads[0]?.nodeId ?? excludeNodeId}`,
      projectId,
      targetId: excludeNodeId,
      targetBackendType,
      sourceTab,
      activeTab: String(activeTab),
      pendingCount: pendingUploads.length,
      visiblePendingCount: filteredPending.length,
      pendingNodeIds: pendingUploads.map((pu) => pu.nodeId).join(','),
      visiblePendingNodeIds: filteredPending.map((pu) => pu.nodeId).join(','),
      pendingMediaTypes: pendingUploads.map((pu) => pu.mediaType).join(','),
      allowedUploadMediaTypes,
    })
  }, [pendingUploads, filteredPending, excludeNodeId, targetBackendType, sourceTab, activeTab, allowedUploadMediaTypes, projectId, referenceCandidateFilter])

  // Selected node IDs set for quick lookup
  const selectedKeys = useMemo(
    () => new Set(selected.map((r) => selectedReferenceKey(r))),
    [selected]
  )

  // Toggle selection
  const toggleSelection = useCallback((candidate: ReferenceCandidate) => {
    // Decide OUTSIDE the state updater. A `setSelected(prev => ...)` callback
    // must be pure: React is free to invoke it twice (StrictMode) or discard
    // and re-run it, so raising a toast in there fired zero, one or two times
    // depending on timing. That nondeterminism showed up as a flaky test long
    // before it would have shown up as a duplicate toast in someone's face.
    //
    // `selectedRef` is the synchronous mirror of `selected` (kept current by
    // the effect below AND written here), so back-to-back clicks read the real
    // current selection instead of a value one render stale.
    const prev = selectedRef.current
    const candidateKey = selectedReferenceKey(candidate)

    if (prev.some((r) => selectedReferenceKey(r) === candidateKey)) {
      const next = prev.filter((r) => selectedReferenceKey(r) !== candidateKey)
      selectedRef.current = next
      setSelected(next)
      libraryAnalytics?.step('asset_library', 'deselect', { option: candidate.mediaType, item_count: next.length })
      // Deselecting frees a slot, so a stale "limit reached" toast would now be
      // wrong. Dismiss by the same stable id we raise it under.
      toast.dismiss(`reference-limit-${candidate.mediaType}`)
      return
    }

    const candidateRef = selectedReferenceFromCandidate(candidate)
    const next = addReferenceWithMediaLimits(
      prev,
      candidateRef,
      maxReferencesByMediaType,
      refuseAtMediaLimit,
    )

    // Identity-unchanged means the add was refused at the cap. Announce it,
    // otherwise the click looks broken — the whole point is that hitting the
    // limit stops being invisible.
    //
    // A toast rather than inline text: the summary row sits on the right of the
    // panel in small type, far from where the user just clicked.
    //
    // `position` overrides the app-wide bottom-right Toaster for this one case
    // (bottom-right is even further from the grid). `id` is stable so hammering
    // a full tab replaces the toast instead of stacking identical copies.
    if (next === prev) {
      toast.error(
        t('referenceSelector.mediaReferenceLimitReached', {
          max: mediaReferenceLimit(maxReferencesByMediaType, candidateRef.mediaType) ?? 0,
        }),
        { id: `reference-limit-${candidateRef.mediaType}`, position: 'top-center' },
      )
      return
    }

    selectedRef.current = next
    setSelected(next)
    libraryAnalytics?.step('asset_library', 'select', { option: candidate.mediaType, item_count: next.length })
  }, [libraryAnalytics, maxReferencesByMediaType, refuseAtMediaLimit, t])

  const summaryTraceId = useSummaryTraceId(isOpen)

  // Remove from summary row
  const removeSelected = useCallback((key: string) => {
    setSelected((prev) => {
      const next = prev.filter((r) => selectedReferenceKey(r) !== key)
      removeBadgeLog({
        event: 'remove-badge-clicked',
        trace_id: summaryTraceId(),
        tray: 'asset',
        node_id: prev.find((r) => selectedReferenceKey(r) === key)?.nodeId ?? null,
        key,
        before: prev.length,
        after: next.length,
      })
      return next
    })
  }, [summaryTraceId])

  // The selected tray is a horizontal scroller, so it clips on both axes. Log
  // the rendered card count alongside the top padding that keeps each card's
  // -top-1.5 remove badge inside the tray's padding box.
  useEffect(() => {
    // The tray only exists once something is selected — don't claim it rendered.
    if (!isOpen || selected.length === 0) return
    removeBadgeLog({
      event: 'summary-tray-rendered',
      trace_id: summaryTraceId(),
      tray: 'asset',
      cards: selected.length,
      scrollerPaddingTopPx: SUMMARY_TRAY_PADDING_TOP_PX,
      badgeOverhangPx: SUMMARY_BADGE_OVERHANG_PX,
    })
  }, [isOpen, selected.length, summaryTraceId])

  // Drag-to-reorder in summary row
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setSelected((prev) => {
      const oldIndex = prev.findIndex((r) => selectedReferenceKey(r) === active.id)
      const newIndex = prev.findIndex((r) => selectedReferenceKey(r) === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [])

  const importLibraryReferences = useCallback(
    async (refs: SelectedReference[]): Promise<SelectedReference[]> => {
      const hasLibraryRefs = refs.some((ref) => referenceSource(ref) === 'library')
      if (!hasLibraryRefs) return refs
      if (!canvas) {
        throw new Error('Canvas context is unavailable for library import')
      }

      const targetNode = canvas.getNode(excludeNodeId)
      const basePosition = targetNode?.position ?? { x: 0, y: 0 }
      const apiBase = getConfiguredApiBase()
      let importIndex = 0

      referenceSyncLog({
        event: 'library-import-start',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        projectId,
        selectedCount: refs.length,
        libraryCount: refs.filter((ref) => referenceSource(ref) === 'library').length,
      })

      const importedByKey = new Map<string, SelectedReference>()
      for (const ref of refs) {
        if (referenceSource(ref) !== 'library') continue

        const resp = await authenticatedFetch(
          `${apiBase}/library/nodes/${ref.nodeId}/import`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target_project_id: projectId }),
          },
        )
        if (!resp.ok) {
          const err = await resp.json().catch(() => null)
          throw new Error(err?.detail || resp.statusText)
        }

        const { node_type, node_data }: ImportReferenceResponse = await resp.json()
        const { data: importNodeData, strippedFields } = stripDirectAssetInputPortWrites(
          node_type,
          node_data,
        )
        if (strippedFields.length > 0) {
          referenceSyncLog({
            event: 'library-import-strip-edge-backed-inputs',
            trace_id: referenceSyncTraceId(excludeNodeId),
            targetId: excludeNodeId,
            projectId,
            importedNodeType: node_type,
            strippedFields,
          })
        }
        const position = {
          x: basePosition.x - 260,
          y: basePosition.y + importIndex * 80,
        }
        const importedNodeId = canvas.addNode(node_type, position, importNodeData)
        const importedCandidate = buildAssetCandidates([
          {
            id: importedNodeId,
            type: node_type,
            data: importNodeData,
            position,
          },
        ]).find((candidate) => (
          candidate.mediaType === ref.mediaType
          && candidate.sourceHandle === ref.sourceHandle
          && candidate.fromIndex === ref.fromIndex
        ))
          ?? buildAssetCandidates([
            {
              id: importedNodeId,
              type: node_type,
              data: importNodeData,
              position,
            },
          ]).find((candidate) => candidate.mediaType === ref.mediaType)

        importedByKey.set(selectedReferenceKey(ref), {
          ...ref,
          nodeId: importedNodeId,
          nodeType: node_type,
          label: importedCandidate?.label ?? ref.label,
          assetPath: importedCandidate?.assetPath ?? ref.assetPath,
          source: 'canvas',
        })
        importIndex += 1
      }

      const resolved = refs.map((ref) => importedByKey.get(selectedReferenceKey(ref)) ?? ref)
      referenceSyncLog({
        event: 'library-import-complete',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        projectId,
        importedCount: importedByKey.size,
        resolvedRefs: summarizeReferences(resolved),
      })
      return resolved
    },
    [canvas, excludeNodeId, projectId],
  )

  // Save: trim to max and call onSave
  const handleSave = useCallback(async () => {
    const trimmed = applySaveReferenceLimits(
      selected,
      maxReferences,
      maxReferencesByMediaType,
    )
    referenceSyncLog({
      event: 'selector-save',
      trace_id: referenceSyncTraceId(excludeNodeId),
      targetId: excludeNodeId,
      mode,
      selectedCount: selected.length,
      savedCount: trimmed.length,
      selectedRefs: summarizeReferences(selected),
      savedRefs: summarizeReferences(trimmed),
    })
    if (mode === 'asset-manager' && onAddToCanvas) {
      libraryAnalytics?.step('asset_library', 'add_to_canvas', { item_count: trimmed.length })
      onAddToCanvas(trimmed)
    } else {
      setIsSaving(true)
      try {
        const resolved = await importLibraryReferences(trimmed)
        onSave(resolved)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err)
        referenceSyncLog({
          event: 'library-import-failed',
          trace_id: referenceSyncTraceId(excludeNodeId),
          targetId: excludeNodeId,
          projectId,
          error: errorMessage,
        })
        toast.error(t('referenceSelector.failedToAddLibraryReference', { message: errorMessage }))
      } finally {
        setIsSaving(false)
      }
    }
  }, [selected, maxReferences, maxReferencesByMediaType, onSave, mode, onAddToCanvas, excludeNodeId, importLibraryReferences, projectId, t, libraryAnalytics])

  // Discard: close without saving
  const handleDiscard = useCallback((trigger = 'close-button-or-backdrop') => {
    if (mode === 'asset-manager') {
      drawerDismissLog({
        event: 'asset-manager-dismissed',
        trigger,
        mode,
        sourceTab,
        activeTab,
      })
    }
    onClose()
  }, [onClose, mode, sourceTab, activeTab])

  const handleBackdropClick = useCallback(() => {
    handleDiscard('backdrop-click')
  }, [handleDiscard])

  const handleCloseButtonClick = useCallback(() => {
    handleDiscard('close-button')
  }, [handleDiscard])

  // Escape key handler — single log via handleDiscard to avoid double-log (review feedback)
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        handleDiscard('escape-key')
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, handleDiscard])

  const isOverTotalLimit = maxReferences !== null && selected.length > maxReferences
  const isOverMediaLimit = isAnyMediaReferenceLimitExceeded(selected, maxReferencesByMediaType)
  const isOverLimit = isOverTotalLimit || isOverMediaLimit
  const selectedMediaSummaries = MEDIA_TABS
    .map((tab) => {
      const max = mediaReferenceLimit(maxReferencesByMediaType, tab.key)
      return max === undefined
        ? null
        : {
            mediaType: tab.key,
            count: referenceCountForMediaType(selected, tab.key),
            max,
          }
    })
    .filter((summary): summary is { mediaType: MediaType; count: number; max: number } => summary !== null)

  if (!isOpen) {
    return shouldTrackReactiveNodes ? (
      <ReactiveNodesSubscriber onNodesChange={handleReactiveNodesChange} />
    ) : null
  }

  const showUploadButton = mode === 'reference' && !!onUploadFile && allowedUploadMediaTypes.length > 0

  const mainContent = createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleBackdropClick()
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Backdrop — clicking outside the modal dismisses it. Previously this div
          intercepted clicks without a handler, so the parent's
          e.target===e.currentTarget check never fired (bug 1573341771089787). */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleBackdropClick}
        data-testid="reference-selector-backdrop"
      />

      {/* Modal */}
      <div
        className="relative z-[10021] flex flex-col bg-background border border-border rounded-xl shadow-2xl"
        style={{ width: '60vw', maxWidth: 900, height: '70vh', maxHeight: 720 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="reference-selector-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground" data-testid="reference-selector-title">
            {mode === 'asset-manager' ? t('referenceSelector.assetSelector') : t('referenceSelector.referenceSelector')}
          </h2>
          <button
            onClick={handleCloseButtonClick}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground/40 hover:text-foreground/80 hover:bg-white/5 transition-colors"
            data-testid="reference-selector-close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-border/50">
          {mode === 'reference' && (
            <div
              className="flex items-center gap-1 rounded-md bg-surface-1 border border-border-subtle p-0.5 mr-2"
              data-testid="source-tab-bar"
            >
              {SOURCE_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleSourceTabChange(tab)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    sourceTab === tab
                      ? 'bg-surface-2 text-foreground'
                      : 'text-text-muted hover:text-foreground/70 hover:bg-surface-2'
                  }`}
                  data-testid={`source-tab-${tab}`}
                >
                  {tab === 'canvas' ? t('referenceSelector.sourceCanvas') : t('referenceSelector.sourceLibrary')}
                </button>
              ))}
            </div>
          )}
          {/* Tabs */}
          {activeTabs.map((tab) => {
            const nodeTypeFilter = NODE_TYPE_TAB_FILTER[tab.key]
            const count =
              tab.key === 'script'
                ? allCandidates.filter((c) => c.nonMediaKind === 'script').length
                : nodeTypeFilter
                  ? allCandidates.filter((c) => c.nodeType === nodeTypeFilter && !c.nonMediaKind).length
                  : allCandidates.filter((c) => c.mediaType === tab.key && !c.nonMediaKind).length
            return (
              <button
                key={tab.key}
                onClick={() => {
                  if (tab.key !== activeTab) libraryAnalytics?.step('asset_library', 'tab', { option: tab.key })
                  setActiveTab(tab.key)
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white/10 text-foreground'
                    : 'text-text-muted hover:text-foreground/70 hover:bg-white/5'
                }`}
                data-testid={`reference-tab-${tab.key}`}
              >
                {tab.icon}
                {/* MEDIA_TABS/LIBRARY_TABS carry an English `label`; localize
                    by the stable `key` (defaultValue keeps the English label).
                    The `nodeTabs` bar below is already localized separately. */}
                {t(`referenceSelector.tab.${tab.key}`, { defaultValue: tab.label })}
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full bg-white/10 text-xxs ${count === 0 ? 'invisible' : ''}`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>

        {/* Grid area */}
        <div className="flex-1 overflow-y-auto px-5 py-3 nowheel" onWheel={(e) => e.stopPropagation()}>
          <h3 className="text-xs font-medium text-text-muted mb-2">
            {sourceTab === 'library' ? t('referenceSelector.inLibrary') : t('referenceSelector.onCanvas')}
          </h3>

          {(filteredCandidates.length > 0 || filteredPending.length > 0) ? (
            <div className="grid grid-cols-4 gap-3" data-testid="reference-grid">
              {/* Pending upload cards at the top */}
              {filteredPending.map((pu) => (
                <div
                  key={`pending-${pu.nodeId}`}
                  className="relative flex flex-col items-center gap-1.5 p-2 rounded-lg border border-white/5 bg-surface-2/50 cursor-not-allowed opacity-70"
                  data-testid={`uploading-card-${pu.nodeId}`}
                >
                  {/* Spinner overlay */}
                  <div className="w-full aspect-square rounded-md bg-black/30 flex items-center justify-center relative overflow-hidden">
                    {pu.previewUrl && pu.mediaType === 'image' && (
                      <img
                        src={pu.previewUrl}
                        alt={pu.fileName}
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                      />
                    )}
                    {pu.previewUrl && pu.mediaType === 'video' && (
                      <video
                        src={pu.previewUrl}
                        preload="metadata"
                        muted
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                      />
                    )}
                    <div className="relative z-10">
                      <svg className="w-6 h-6 animate-spin text-foreground/50" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-text-secondary truncate w-full text-center">
                    {pu.fileName}
                  </span>
                </div>
              ))}

              {/* Normal candidate cards */}
              {filteredCandidates.map((candidate) => {
                const isSelected = selectedKeys.has(selectedReferenceKey(candidate))
                return (
                  <button
                    key={selectedReferenceKey(candidate)}
                    onClick={() => toggleSelection(candidate)}
                    className={`group relative flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all border ${
                      isSelected
                        ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30'
                        : 'border-white/5 hover:border-border hover:bg-surface-2'
                    }`}
                    data-testid={candidate.fromIndex === undefined
                      ? `reference-card-${candidate.nodeId}`
                      : `reference-card-${candidate.nodeId}-${candidate.fromIndex}`}
                  >
                    {/* Checkmark overlay */}
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {/* Delete from library button */}
                    {mode === 'asset-manager' && sourceTab === 'library' && !isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          libraryAnalytics?.step('asset_library', 'delete', { option: candidate.mediaType })
                          setPendingDeleteNodeId(candidate.nodeId)
                        }}
                        className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-surface-2 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80 z-10"
                        title={t('referenceSelector.removeFromLibrary')}
                        data-testid={`reference-card-delete-${candidate.nodeId}`}
                      >
                        <svg className="w-3 h-3 text-foreground/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}

                    {/* Thumbnail */}
                    {candidate.nonMediaKind === 'script' ? (
                      <div
                        className="w-full aspect-square rounded-md bg-surface-2 border border-white/5 p-2 overflow-hidden text-left"
                        data-testid={`reference-card-text-preview-${candidate.nodeId}`}
                      >
                        <p className="text-xxs leading-snug text-text-muted line-clamp-6 whitespace-pre-wrap break-words">
                          {candidate.textPreview || t('referenceSelector.script')}
                        </p>
                      </div>
                    ) : candidate.mediaType === 'image' ? (
                      <img
                        src={resolveAssetPreviewUrl(projectId, candidate.assetPath, sourceTab === 'library') ?? undefined}
                        alt={candidate.label}
                        className="w-full aspect-square rounded-md object-cover bg-black/30"
                        loading="lazy"
                      />
                    ) : candidate.mediaType === 'video' ? (
                      <VideoReferenceThumbnail
                        projectId={projectId}
                        nodeId={candidate.nodeId}
                        assetPath={candidate.assetPath}
                        alt={candidate.label}
                        className="w-full aspect-square rounded-md object-cover bg-black/30"
                        surface="node-selector-grid"
                        isLibrary={sourceTab === 'library'}
                      />
                    ) : (
                      <AudioReferenceThumbnail
                        label={candidate.label}
                        url={resolveAssetPreviewUrl(projectId, candidate.assetPath, sourceTab === 'library')}
                        barCount={AUDIO_GRID_BAR_COUNT}
                        referenceNodeId={candidate.nodeId}
                        surface="node-selector-grid"
                        labelPlacement="none"
                        testId={`reference-card-audio-thumbnail-${candidate.nodeId}`}
                        className="w-full rounded-md"
                      />
                    )}

                    {/* Label */}
                    <span className="text-xs font-medium text-text-secondary truncate w-full text-center">
                      {candidate.label}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : sourceTab === 'library' ? (
            <div className="py-12 text-center" data-testid="library-empty-state">
              <svg className="w-10 h-10 mx-auto text-foreground/10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <p className="text-sm text-foreground/30">
                {t('referenceSelector.noSavedAssets')}
              </p>
            </div>
          ) : (
            <div className="py-12 text-center">
              <svg className="w-10 h-10 mx-auto text-foreground/10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-foreground/30" data-testid="reference-empty-state">
                {activeTab === 'image' ? t('referenceSelector.noImagesOnCanvas') : activeTab === 'video' ? t('referenceSelector.noVideosOnCanvas') : t('referenceSelector.noAudioOnCanvas')}
              </p>
            </div>
          )}

        </div>

        {/* Summary row */}
        <div className="border-t border-border px-5 py-3" data-testid="reference-summary">
          <div className="flex items-center justify-between mb-2">
            {selectedMediaSummaries.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                {selectedMediaSummaries.map((summary) => (
                  <span
                    key={summary.mediaType}
                    className="text-xs font-medium text-text-muted"
                    data-testid={`reference-selected-${summary.mediaType}-count`}
                  >
                    {t(MEDIA_SELECTED_COUNT_KEYS[summary.mediaType], {
                      count: summary.count,
                      max: summary.max,
                    })}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-xs font-medium text-text-muted">
                {t('referenceSelector.selectedCount', {
                  count: selected.length,
                  max: maxReferences ?? '∞',
                })}
              </span>
            )}
            {reservedStyleSlots !== undefined && reservedStyleSlots > 0 && !isOverLimit && (
              <span className="text-xxs text-text-muted" data-testid="reserved-style-slots">
                {t('referenceSelector.reservedStyleSlots', { count: reservedStyleSlots })}
              </span>
            )}
            {isOverLimit && (
              <span className="text-xxs text-warning" data-testid="reference-limit-warning">
                {isOverTotalLimit
                  ? t('referenceSelector.maxReferencesWarning', { max: maxReferences })
                  : isOverMediaLimit
                    ? t('referenceSelector.maxMediaReferencesWarning')
                    : null}
              </span>
            )}
            {/* A refusal is NOT rendered here. It is a different state from
                over-limit — the selection is legal (count == cap), so
                `isOverLimit` is false — and this row is small type on the right
                of the panel, far from the grid the user just clicked. It goes
                out as a top-center toast from `toggleSelection` instead. */}
          </div>

          {selected.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selected.map((r) => selectedReferenceKey(r))}
                strategy={horizontalListSortingStrategy}
              >
                <div
                  className={`flex gap-2 overflow-x-auto ${SUMMARY_TRAY_PADDING_TOP_CLASS} pb-1 nowheel`}
                  data-testid="reference-summary-scroller"
                  onWheel={(e) => e.stopPropagation()}
                >
                  {selected.map((ref, index) => (
                    <SortableSummaryCard
                      key={selectedReferenceKey(ref)}
                      reference={ref}
                      projectId={projectId}
                      onRemove={removeSelected}
                      isDimmed={
                        (maxReferences !== null && index >= maxReferences) ||
                        isReferenceOverMediaLimit(selected, index, maxReferencesByMediaType)
                      }
                      isLibrary={referenceSource(ref) === 'library'}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <p className="text-xs font-medium text-foreground/20 py-3 text-center" data-testid="reference-no-selection">
              {t('referenceSelector.noReferencesSelected')}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center ${showUploadButton ? 'justify-between' : 'justify-end'} px-5 py-3 border-t border-border/50`}>
          {/* Upload button (reference mode only) */}
          {showUploadButton && (
            <UploadFlyout
              onUploadFile={handleUpload}
              allowedTypes={allowedUploadMediaTypes}
              size="sm"
              testIdPrefix="refselector-upload"
            />
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || isOverLimit}
            className="px-4 py-2 text-sm rounded-lg bg-primary/90 hover:bg-primary text-primary-foreground font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="reference-save-btn"
          >
            {isSaving ? t('referenceSelector.adding') : mode === 'asset-manager' ? t('referenceSelector.addToCanvas') : t('referenceSelector.saveAndClose')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )

  // Delete confirmation dialog (separate portal at higher z-index)
  const deleteDialog = pendingDeleteNodeId ? createPortal(
    <div
      className="fixed inset-0 z-[10030] flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) setPendingDeleteNodeId(null) }}
      data-testid="delete-confirmation-backdrop"
    >
      <div
        className="relative w-full max-w-md bg-surface-1 rounded-lg border border-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-confirmation-dialog"
      >
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-foreground">{t('referenceSelector.deleteAsset')}</h2>
        </div>
        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-4">
            {t('referenceSelector.deleteAssetConfirmation')}
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                libraryAnalytics?.step('asset_library', 'delete_cancelled')
                setPendingDeleteNodeId(null)
              }}
              className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-2 transition-colors"
              data-testid="delete-confirmation-cancel"
            >
              {t('referenceSelector.cancel')}
            </button>
            <button
              onClick={() => {
                libraryAnalytics?.step('asset_library', 'delete_confirmed')
                onDeleteFromLibrary?.(pendingDeleteNodeId)
                setSelected((prev) => prev.filter((r) => r.nodeId !== pendingDeleteNodeId))
                setPendingDeleteNodeId(null)
              }}
              className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
              data-testid="delete-confirmation-confirm"
            >
              {t('referenceSelector.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null

  return (
    <>
      {shouldTrackReactiveNodes && (
        <ReactiveNodesSubscriber onNodesChange={handleReactiveNodesChange} />
      )}
      {mainContent}
      {deleteDialog}
    </>
  )
})

AssetReferenceSelector.displayName = 'AssetReferenceSelector'

type NodeSelectorCandidate = MentionCandidate
const ALL_NODE_TYPES_TAB = '__all__'

function selectedNodeReferenceKey(reference: Pick<SelectedNodeReference, 'nodeId'>): string {
  return reference.nodeId
}

function selectedNodeFromCandidate(candidate: NodeSelectorCandidate): SelectedNodeReference {
  return {
    nodeId: candidate.id,
    nodeType: candidate.type,
    label: candidate.label,
    thumbnailPath: candidate.thumbnailPath,
  }
}

function nodeTypeLabel(nodeType: string): string {
  return nodeSchemas[nodeType]?.label ?? (nodeType || 'Node')
}

const SortableNodeSummaryCard = memo(function SortableNodeSummaryCard({
  reference,
  projectId,
  onRemove,
  isDimmed,
}: {
  reference: SelectedNodeReference
  projectId: string
  onRemove: (key: string) => void
  isDimmed: boolean
}) {
  const { t } = useTranslation('canvas')
  const sortableId = selectedNodeReferenceKey(reference)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : isDimmed ? 0.35 : 1,
    zIndex: isDragging ? 10 : undefined,
  }
  const thumbnailUrl = reference.thumbnailPath
    ? getThumbnailUrl(projectId, reference.thumbnailPath, 0)
    : null

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex w-24 flex-shrink-0 cursor-grab select-none flex-col items-center gap-1 rounded-lg border p-1.5 active:cursor-grabbing ${
        isDimmed
          ? 'border-white/5 bg-white/[0.02]'
          : 'border-white/10 bg-surface-1 hover:border-border'
      } ${isDragging ? 'shadow shadow-black/30' : ''}`}
      data-testid={`reference-node-summary-card-${reference.nodeId}`}
      {...attributes}
      {...listeners}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={reference.label}
          className="h-14 w-14 rounded object-cover bg-black/30"
        />
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded bg-surface-2 text-muted-foreground [&>svg]:h-6 [&>svg]:w-6">
          {NODE_TYPE_ICON[reference.nodeType] ?? NODE_TYPE_ICON.nodeGroup}
        </div>
      )}
      <span className="w-full truncate text-center text-xxs leading-tight text-text-secondary">
        {reference.label}
      </span>
      <button
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          onRemove(sortableId)
        }}
        onPointerDown={(event) => event.stopPropagation()}
        className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-white/20 bg-surface-2 opacity-0 transition-opacity hover:bg-destructive/80 group-hover:opacity-100"
        title={t('referenceSelector.remove')}
      >
        <svg className="h-2.5 w-2.5 text-foreground/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
})
SortableNodeSummaryCard.displayName = 'SortableNodeSummaryCard'

function NodeReferenceSelector({
  isOpen,
  onClose,
  canvasNodes,
  excludeNodeId,
  currentNodeSelections = [],
  maxReferences = 99,
  onSaveNodeSelections,
  nodeSelectorFilter,
  nodeSelectorTitle,
  nodeSelectorEmptyLabel,
  nodeSelectorNoSelectionLabel,
  onUploadFile,
}: ReferenceSelectorProps) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const [selected, setSelected] = useState<SelectedNodeReference[]>([])
  const [activeNodeType, setActiveNodeType] = useState<string>(ALL_NODE_TYPES_TAB)
  const initializedRef = useRef(false)
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])
  const [reactiveNodes, setReactiveNodes] = useState<ReactFlowCanvasNode[]>(EMPTY_REACTIVE_NODES)
  const [reactiveNodesReady, setReactiveNodesReady] = useState(false)
  const effectiveMaxReferences = maxReferences
  const shouldTrackReactiveNodes = isOpen || pendingUploads.length > 0

  const handleReactiveNodesChange = useCallback((nextNodes: ReactFlowCanvasNode[]) => {
    setReactiveNodes((prevNodes) => {
      if (
        prevNodes.length === nextNodes.length &&
        prevNodes.every((node, index) => node === nextNodes[index])
      ) {
        return prevNodes
      }
      return nextNodes
    })
    setReactiveNodesReady(true)
  }, [])

  useEffect(() => {
    if (!shouldTrackReactiveNodes) {
      setReactiveNodes(EMPTY_REACTIVE_NODES)
      setReactiveNodesReady(false)
    }
  }, [shouldTrackReactiveNodes])

  const effectiveCanvasNodes = shouldTrackReactiveNodes && reactiveNodesReady ? reactiveNodes : canvasNodes

  const candidates = useMemo(() => {
    if (!isOpen) return []
    const filteredNodes = effectiveCanvasNodes.filter((node) => (
      node.id !== excludeNodeId && (nodeSelectorFilter ? nodeSelectorFilter(node) : true)
    ))
    return buildCandidates(filteredNodes)
  }, [effectiveCanvasNodes, excludeNodeId, isOpen, nodeSelectorFilter])
  const candidatesById = useMemo(
    () => new Map(candidates.map((candidate) => [candidate.id, candidate])),
    [candidates],
  )

  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      const refreshed = currentNodeSelections.map((selection) => {
        const fresh = candidatesById.get(selection.nodeId)
        return fresh ? selectedNodeFromCandidate(fresh) : selection
      })
      setSelected(refreshed)
      setActiveNodeType(ALL_NODE_TYPES_TAB)
      setPendingUploads((prev) => {
        for (const pendingUpload of prev) {
          if (pendingUpload.previewUrl) URL.revokeObjectURL(pendingUpload.previewUrl)
        }
        return []
      })
      initializedRef.current = true
    } else if (!isOpen) {
      initializedRef.current = false
    }
  }, [candidatesById, currentNodeSelections, isOpen])

  const nodeTabs = useMemo(() => {
    const counts = new Map<string, number>()
    for (const candidate of candidates) {
      counts.set(candidate.type, (counts.get(candidate.type) ?? 0) + 1)
    }
    return [
      {
        key: ALL_NODE_TYPES_TAB,
        label: t('referenceSelector.allNodes'),
        icon: NODE_TYPE_ICON.nodeGroup,
        count: candidates.length,
      },
      ...Array.from(counts.entries())
        .sort(([left], [right]) => nodeTypeLabel(left).localeCompare(nodeTypeLabel(right)))
        .map(([nodeType, count]) => ({
          key: nodeType,
          label: nodeTypeLabel(nodeType),
          icon: NODE_TYPE_ICON[nodeType] ?? NODE_TYPE_ICON.nodeGroup,
          count,
        })),
    ]
  }, [candidates, t])

  useEffect(() => {
    if (!nodeTabs.some((tab) => tab.key === activeNodeType)) {
      setActiveNodeType(ALL_NODE_TYPES_TAB)
    }
  }, [activeNodeType, nodeTabs])

  const filteredCandidates = useMemo(
    () => activeNodeType === ALL_NODE_TYPES_TAB
      ? candidates
      : candidates.filter((candidate) => candidate.type === activeNodeType),
    [activeNodeType, candidates],
  )
  const selectedKeys = useMemo(
    () => new Set(selected.map(selectedNodeReferenceKey)),
    [selected],
  )

  useEffect(() => {
    if (pendingUploads.length === 0) return

    const remaining: PendingUpload[] = []
    const completedSelections: SelectedNodeReference[] = []
    let changed = false

    for (const pendingUpload of pendingUploads) {
      const node = effectiveCanvasNodes.find((candidateNode) => candidateNode.id === pendingUpload.nodeId)
      const nodeStatus = typeof node?.data?.status === 'string' ? node.data.status : undefined

      if (node && (!nodeStatus || nodeStatus === 'completed')) {
        const isAllowed = !nodeSelectorFilter || nodeSelectorFilter(node)
        const candidate = isAllowed ? buildCandidates([node])[0] : undefined
        if (candidate) {
          if (pendingUpload.previewUrl) URL.revokeObjectURL(pendingUpload.previewUrl)
          completedSelections.push(selectedNodeFromCandidate(candidate))
          changed = true
          continue
        }
        if (pendingUpload.previewUrl) URL.revokeObjectURL(pendingUpload.previewUrl)
        changed = true
        continue
      }

      if (nodeStatus === 'error') {
        if (pendingUpload.previewUrl) URL.revokeObjectURL(pendingUpload.previewUrl)
        changed = true
        continue
      }

      remaining.push(pendingUpload)
    }

    if (changed) {
      setPendingUploads(remaining)
    }

    if (completedSelections.length === 0) return

    setSelected((prev) => {
      const existingIds = new Set(prev.map(selectedNodeReferenceKey))
      const next = [...prev]
      for (const completedSelection of completedSelections) {
        if (!existingIds.has(selectedNodeReferenceKey(completedSelection))) {
          next.push(completedSelection)
          existingIds.add(selectedNodeReferenceKey(completedSelection))
        }
      }
      return next
    })
  }, [effectiveCanvasNodes, nodeSelectorFilter, pendingUploads])

  const toggleSelection = useCallback((candidate: NodeSelectorCandidate) => {
    setSelected((prev) => {
      const exists = prev.some((reference) => reference.nodeId === candidate.id)
      if (exists) {
        return prev.filter((reference) => reference.nodeId !== candidate.id)
      }
      if (effectiveMaxReferences === 1) {
        return [selectedNodeFromCandidate(candidate)]
      }
      return [...prev, selectedNodeFromCandidate(candidate)]
    })
  }, [effectiveMaxReferences])

  const handleUpload = useCallback((file: File, type: 'image' | 'video' | 'audio') => {
    if (!onUploadFile) return
    const nodeId = onUploadFile(file, type)
    const previewUrl = file.type.startsWith('image/') || file.type.startsWith('video/')
      ? URL.createObjectURL(file)
      : undefined
    setPendingUploads((prev) => [
      ...prev,
      { nodeId, mediaType: type, fileName: file.name, previewUrl },
    ])
  }, [onUploadFile])

  const summaryTraceId = useSummaryTraceId(isOpen)

  const removeSelected = useCallback((key: string) => {
    setSelected((prev) => {
      const next = prev.filter((reference) => selectedNodeReferenceKey(reference) !== key)
      removeBadgeLog({
        event: 'remove-badge-clicked',
        trace_id: summaryTraceId(),
        tray: 'node',
        node_id: prev.find((r) => selectedNodeReferenceKey(r) === key)?.nodeId ?? null,
        key,
        before: prev.length,
        after: next.length,
      })
      return next
    })
  }, [summaryTraceId])

  useEffect(() => {
    // The tray only exists once something is selected — don't claim it rendered.
    if (!isOpen || selected.length === 0) return
    removeBadgeLog({
      event: 'summary-tray-rendered',
      trace_id: summaryTraceId(),
      tray: 'node',
      cards: selected.length,
      scrollerPaddingTopPx: SUMMARY_TRAY_PADDING_TOP_PX,
      badgeOverhangPx: SUMMARY_BADGE_OVERHANG_PX,
    })
  }, [isOpen, selected.length, summaryTraceId])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setSelected((prev) => {
      const oldIndex = prev.findIndex((reference) => selectedNodeReferenceKey(reference) === active.id)
      const newIndex = prev.findIndex((reference) => selectedNodeReferenceKey(reference) === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [])

  const handleSave = useCallback(() => {
    onSaveNodeSelections?.(selected.slice(0, effectiveMaxReferences ?? selected.length))
  }, [effectiveMaxReferences, onSaveNodeSelections, selected])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, onClose])

  const isOverLimit = effectiveMaxReferences !== null
    && selected.length > effectiveMaxReferences

  if (!isOpen) {
    if (shouldTrackReactiveNodes) {
      return <ReactiveNodesSubscriber onNodesChange={handleReactiveNodesChange} />
    }
    return null
  }

  return (
    <>
      <ReactiveNodesSubscriber onNodesChange={handleReactiveNodesChange} />
      {createPortal(
        <div
          className="fixed inset-0 z-[10020] flex items-center justify-center"
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose()
          }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            data-testid="reference-selector-backdrop"
          />
          <div
            className="relative z-[10021] flex flex-col rounded-xl border border-border bg-background shadow-2xl"
            style={{ width: '60vw', maxWidth: 900, height: '70vh', maxHeight: 720 }}
            onClick={(event) => event.stopPropagation()}
            data-testid="reference-selector-modal"
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
              <h2 className="text-sm font-semibold text-foreground" data-testid="reference-selector-title">
                {nodeSelectorTitle ?? t('referenceSelector.nodeSelector')}
              </h2>
              <button
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-md text-foreground/40 transition-colors hover:bg-white/5 hover:text-foreground/80"
                data-testid="reference-selector-close"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-1 overflow-x-auto border-b border-border/50 px-5 py-2">
              {nodeTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveNodeType(tab.key)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeNodeType === tab.key
                      ? 'bg-white/10 text-foreground'
                      : 'text-text-muted hover:bg-white/5 hover:text-foreground/70'
                  }`}
                  data-testid={`reference-node-tab-${tab.key}`}
                >
                  <span className="[&>svg]:h-4 [&>svg]:w-4">{tab.icon}</span>
                  {tab.label}
                  <span className={`ml-0.5 rounded-full bg-white/10 px-1.5 py-0.5 text-xxs ${tab.count === 0 ? 'invisible' : ''}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="nowheel flex-1 overflow-y-auto px-5 py-3" onWheel={(event) => event.stopPropagation()}>
              <h3 className="mb-2 text-xs font-medium text-text-muted">
                {t('referenceSelector.onCanvas')}
              </h3>
              {filteredCandidates.length > 0 ? (
                <div className="grid grid-cols-4 gap-3" data-testid="reference-grid">
                  {filteredCandidates.map((candidate) => {
                    const isSelected = selectedKeys.has(candidate.id)
                    const thumbnailUrl = candidate.thumbnailPath
                      ? getThumbnailUrl(projectId, candidate.thumbnailPath, 0)
                      : null
                    return (
                      <button
                        key={candidate.id}
                        onClick={() => toggleSelection(candidate)}
                        className={`group relative flex flex-col items-center gap-1.5 rounded-lg border p-2 transition-all ${
                          isSelected
                            ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30'
                            : 'border-white/5 hover:border-border hover:bg-surface-2'
                        }`}
                        data-testid={`reference-node-card-${candidate.id}`}
                      >
                        {isSelected && (
                          <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={candidate.label}
                            className="aspect-square w-full rounded-md object-cover bg-black/30"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center rounded-md border border-white/5 bg-surface-2 text-muted-foreground [&>svg]:h-8 [&>svg]:w-8">
                            {NODE_TYPE_ICON[candidate.type] ?? NODE_TYPE_ICON.nodeGroup}
                          </div>
                        )}
                        <span className="w-full truncate text-center text-xs font-medium text-text-secondary">
                          {candidate.label}
                        </span>
                        <span className="w-full truncate text-center text-xxs text-text-muted">
                          {nodeTypeLabel(candidate.type)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <svg className="mx-auto mb-3 h-10 w-10 text-foreground/10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  <p className="text-sm text-foreground/30" data-testid="reference-empty-state">
                    {nodeSelectorEmptyLabel ?? t('referenceSelector.noSourceNodes')}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-border px-5 py-3" data-testid="reference-summary">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-text-muted">
                  {t('referenceSelector.selectedCount', {
                    count: selected.length,
                    max: effectiveMaxReferences ?? '∞',
                  })}
                </span>
                {isOverLimit && (
                  <span className="text-xxs text-warning" data-testid="reference-limit-warning">
                    {t('referenceSelector.maxReferencesWarning', { max: effectiveMaxReferences })}
                  </span>
                )}
              </div>

              {selected.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selected.map(selectedNodeReferenceKey)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div
                      className={`nowheel flex gap-2 overflow-x-auto ${SUMMARY_TRAY_PADDING_TOP_CLASS} pb-1`}
                      data-testid="reference-node-summary-scroller"
                      onWheel={(event) => event.stopPropagation()}
                    >
                      {selected.map((reference, index) => (
                        <SortableNodeSummaryCard
                          key={selectedNodeReferenceKey(reference)}
                          reference={reference}
                          projectId={projectId}
                          onRemove={removeSelected}
                          isDimmed={
                            effectiveMaxReferences !== null
                            && index >= effectiveMaxReferences
                          }
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="py-3 text-center text-xs font-medium text-foreground/20" data-testid="reference-no-selection">
                  {nodeSelectorNoSelectionLabel ?? t('referenceSelector.noSourceNodesSelected')}
                </p>
              )}
            </div>

            <div
              className={`flex items-center ${
                onUploadFile ? 'justify-between' : 'justify-end'
              } border-t border-border/50 px-5 py-3`}
            >
              {onUploadFile && (
                <UploadFlyout
                  onUploadFile={handleUpload}
                  allowedTypes={ALL_MEDIA_TYPES}
                  size="sm"
                  testIdPrefix="refselector-upload"
                />
              )}
              <button
                onClick={handleSave}
                className="rounded-lg bg-primary/90 px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary"
                data-testid="reference-save-btn"
              >
                {t('referenceSelector.saveAndClose')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

const ReferenceSelector = memo(function ReferenceSelector(props: ReferenceSelectorProps) {
  if (props.mode === 'node-selector') {
    return <NodeReferenceSelector {...props} />
  }
  return <AssetReferenceSelector {...props} />
})

ReferenceSelector.displayName = 'ReferenceSelector'
export { ReferenceSelector }
export type {
  SelectedReference as ReferenceSelectorSelection,
  SelectedNodeReference as ReferenceSelectorNodeSelection,
}
